// A second "are you sure?" step for one-tap actions that are easy to press by mistake
// (Done, Reopen, Ignore on My Today items).
//   const [confirm, confirmDialog] = useConfirmAction();
//   if (await confirm({ title: 'Mark as done?', body: '…', confirmLabel: 'Yes, mark done' })) doIt();
//   …render {confirmDialog} once in the component.
import React, { useCallback, useRef, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';

export type ConfirmOptions = {
  title: string;
  /** Who or what the action is about, shown in bold. */
  subject?: string;
  body?: string;
  confirmLabel: string;
  tone?: 'ok' | 'danger' | 'gold';
};

const TONE = { ok: '#2f6b35', danger: '#b3261e', gold: '#9d6f32' };

export function useConfirmAction(): [(opts: ConfirmOptions) => Promise<boolean>, React.ReactNode] {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((ok: boolean) => void) | null>(null);

  const confirm = useCallback((next: ConfirmOptions) => new Promise<boolean>((resolve) => {
    resolver.current?.(false);
    resolver.current = resolve;
    setOpts(next);
  }), []);

  const close = (ok: boolean) => {
    resolver.current?.(ok);
    resolver.current = null;
    setOpts(null);
  };

  const color = TONE[opts?.tone || 'ok'];
  const dialog = <Dialog open={!!opts} onClose={() => close(false)} maxWidth="xs" fullWidth>
    <DialogTitle sx={{ fontWeight: 700, fontSize: 17, pb: 1 }}>{opts?.title}</DialogTitle>
    <DialogContent sx={{ pb: 1 }}>
      {opts?.subject && <Typography sx={{ fontWeight: 700, fontSize: 14.5, color: '#1a1a1a' }}>{opts.subject}</Typography>}
      {opts?.body && <Typography sx={{ fontSize: 13.5, color: '#5f5f5f', mt: opts?.subject ? 0.5 : 0 }}>{opts.body}</Typography>}
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
      {/* Cancel is the default, so Enter or a stray tap does not repeat the mistake. */}
      <Button autoFocus onClick={() => close(false)} sx={{ color: '#5f5f5f', textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
      <Box component="span">
        <Button variant="contained" onClick={() => close(true)} disableElevation
          sx={{ bgcolor: color, '&:hover': { bgcolor: color, filter: 'brightness(0.92)' }, textTransform: 'none', fontWeight: 700 }}>
          {opts?.confirmLabel}
        </Button>
      </Box>
    </DialogActions>
  </Dialog>;

  return [confirm, dialog];
}
